import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, LogOut, Globe, Lock, LockKeyhole, Star, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useApp } from '@/contexts/AppContext';
import { useProject } from '@/contexts/ProjectContext';
import { useProjectSync } from '@/hooks/useProjectSync';
import { projectService } from '@/services/projectService';
import { queryKeys } from '@/lib/queryKeys';
import { normalizeData } from '@/lib/parsers';
import { formatProjectVersionState, lockedWriteBackMessage } from '@/lib/projectVersionState';
import { toast } from 'sonner';
import type { LocalProjectSummary, ProjectVersionOverview } from '@/types/project';

const BACKEND_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';
const PAGE_SIZE = 25;

export interface UnifiedProject {
  projectId: number;
  versionId: number | null;
  name: string;
  department: string | null;
  technicalOwner: string | null;
  confidentiality: LocalProjectSummary['confidentiality'];
  internetFacing: boolean | null;
  state: LocalProjectSummary['state'];
  isLocked: boolean;
  local: LocalProjectSummary | null;
  remote: ProjectVersionOverview | null;
}

export function mergeProjects(
  local: LocalProjectSummary[],
  remote: ProjectVersionOverview[],
): UnifiedProject[] {
  const merged = new Map<number, UnifiedProject>();
  for (const project of local) {
    const synchronized = project.cache_state === 'synchronized';
    merged.set(project.project_id, {
      projectId: project.project_id,
      versionId: project.project_version_id,
      name: project.name,
      department: synchronized ? project.department : null,
      technicalOwner: synchronized ? project.technical_owner : null,
      confidentiality: synchronized ? project.confidentiality : null,
      internetFacing: synchronized ? project.internet_facing : null,
      state: project.state ?? null,
      isLocked: project.is_locked ?? false,
      local: project,
      remote: null,
    });
  }
  for (const project of remote) {
    const existing = merged.get(project.project_id);
    merged.set(project.project_id, {
      projectId: project.project_id,
      versionId: project.id,
      name: project.name,
      department: existing?.department ?? null,
      technicalOwner: existing?.technicalOwner ?? null,
      confidentiality: existing?.confidentiality ?? null,
      internetFacing: existing?.internetFacing ?? null,
      state: project.state ?? existing?.state ?? null,
      isLocked: project.is_locked ?? existing?.isLocked ?? false,
      local: existing?.local ?? null,
      remote: project,
    });
  }
  return [...merged.values()].sort((a, b) => {
    const aFavorite = a.local?.is_favorite ?? a.remote?.is_favorite ?? false;
    const bFavorite = b.local?.is_favorite ?? b.remote?.is_favorite ?? false;
    if (aFavorite !== bFavorite) return aFavorite ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

interface ProjectPickerProps {
  localProjects: LocalProjectSummary[];
  localLoading: boolean;
  localError: Error | null;
  onConnect: () => void;
  onDisconnect?: () => void;
}

export default function ProjectPicker({
  localProjects,
  localLoading,
  localError,
  onConnect,
  onDisconnect,
}: ProjectPickerProps) {
  const { projects, isConnected, cookie } = useThreatCat();
  const { selectProject } = useProject();
  const { setAppState } = useApp();
  const { syncProject } = useProjectSync();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const opening = useRef(false);
  const mounted = useRef(true);
  const [openingProjectId, setOpeningProjectId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [localFavorites, setLocalFavorites] = useState<Set<number>>(
    () => new Set(
      [...localProjects, ...projects]
        .filter((project) => project.is_favorite)
        .map((project) => project.project_id),
    ),
  );

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setLocalFavorites(new Set(
      [...localProjects, ...projects]
        .filter((project) => project.is_favorite)
        .map((project) => project.project_id),
    ));
  }, [localProjects, projects]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const toggleFavorite = async (projectId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const isFav = localFavorites.has(projectId);
    const method = isFav ? 'DELETE' : 'PUT';

    setLocalFavorites((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(projectId);
      else next.add(projectId);
      return next;
    });

    try {
      await fetch(`${BACKEND_BASE}/api/favorites/${projectId}`, {
        method,
        credentials: 'include',
      });
    } catch {
      setLocalFavorites((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(projectId);
        else next.delete(projectId);
        return next;
      });
    }
  };

  const mergedProjects = useMemo(
    () => mergeProjects(localProjects, projects),
    [localProjects, projects],
  );
  const filtered = useMemo(
    () =>
      mergedProjects
        .filter((project) => {
          const needle = search.toLowerCase();
          return project.name.toLowerCase().includes(needle) ||
            project.technicalOwner?.toLowerCase().includes(needle) ||
            project.department?.toLowerCase().includes(needle);
        })
        .sort((a, b) => {
          const aFavorite = localFavorites.has(a.projectId);
          const bFavorite = localFavorites.has(b.projectId);
          if (aFavorite !== bFavorite) return aFavorite ? -1 : 1;
          return a.name.localeCompare(b.name);
        }),
    [mergedProjects, search, localFavorites],
  );

  const favoriteProjects = useMemo(
    () => filtered.filter((project) => localFavorites.has(project.projectId)),
    [filtered, localFavorites],
  );
  const otherProjects = useMemo(
    () => filtered.filter((project) => !localFavorites.has(project.projectId)),
    [filtered, localFavorites],
  );

  const totalPages = Math.max(1, Math.ceil(otherProjects.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = otherProjects.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const rangeStart = otherProjects.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, otherProjects.length);

  const handleSelect = async (project: UnifiedProject) => {
    if (opening.current) return;
    opening.current = true;
    setOpeningProjectId(project.projectId);
    const shouldSync = isConnected && project.versionId !== null;
    const toastId = toast.loading(shouldSync ? 'Synchronizing project...' : 'Opening project...');
    try {
      const detailKey = queryKeys.projects.detail(project.projectId);
      const loadedDetail = shouldSync && project.versionId !== null
        ? await syncProject({ projectId: project.projectId, projectVersionId: project.versionId })
        : await queryClient.fetchQuery({
          queryKey: detailKey,
          queryFn: () => projectService.get(project.projectId),
        });
      if (!loadedDetail.snapshot) {
        throw new Error(shouldSync
          ? 'Synchronization did not produce a project snapshot.'
          : 'Connect to ThreatCat to synchronize this project.');
      }
      const appState = normalizeData(loadedDetail.snapshot);
      toast.dismiss(toastId);
      if (!mounted.current) return;
      selectProject(loadedDetail);
      setAppState(appState);
      navigate(`/projects/${project.projectId}`, {
        state: {
          remoteVersionId: project.remote?.id,
          synchronizedDetailUpdatedAt: shouldSync
            ? queryClient.getQueryState(detailKey)?.dataUpdatedAt
            : undefined,
        },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to open project', { id: toastId });
    } finally {
      opening.current = false;
      if (mounted.current) setOpeningProjectId(null);
    }
  };

  const renderProjectRow = (project: UnifiedProject) => {
    const local = project.local;
    const remote = project.remote;
    const isFavorite = localFavorites.has(project.projectId);
    const stateLabel = formatProjectVersionState(project.state);

    return (
      <tr
        key={project.projectId}
        className={openingProjectId !== null ? 'cursor-wait' : 'cursor-pointer hover:bg-muted/50'}
        aria-disabled={openingProjectId !== null}
        aria-busy={openingProjectId === project.projectId}
        onClick={() => void handleSelect(project)}
      >
        <td className="px-4 py-3 text-center">
          <button
            onClick={(e) => toggleFavorite(project.projectId, e)}
            className="text-muted-foreground hover:text-yellow-500 transition-colors"
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            {isFavorite
              ? <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
              : <Star className="h-4 w-4" />}
          </button>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium">{project.name}</span>
            {remote?.leanix_entries.map((entry) => (
              <span
                key={entry.app_id}
                className="text-xs bg-primary/10 text-primary border border-primary/20 rounded px-1.5 py-0.5 font-mono shrink-0"
              >
                {entry.app_id}
              </span>
            ))}
          </div>
        </td>
        <td className="px-4 py-3 text-muted-foreground">{project.technicalOwner || '—'}</td>
        <td className="px-4 py-3 text-muted-foreground">{project.department || '—'}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {project.confidentiality && (
              <span className="flex items-center gap-1 text-muted-foreground" title={`Confidentiality: ${project.confidentiality}`}>
                <Lock className="h-3.5 w-3.5" />
              </span>
            )}
            {project.internetFacing === true && (
              <span className="flex items-center gap-1 text-orange-500" title="Internet-facing">
                <Globe className="h-3.5 w-3.5" />
              </span>
            )}
            {project.internetFacing === false && (
              <span className="sr-only">Not Internet-facing</span>
            )}
            {!project.confidentiality && project.internetFacing == null && '—'}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs bg-muted text-muted-foreground border rounded px-1.5 py-0.5">
              {remote ? `v${remote.version}` : project.versionId ? `Version ${project.versionId}` : '—'}
            </span>
            {stateLabel && (
              <span className="text-xs bg-muted/60 text-foreground border rounded px-1.5 py-0.5">
                {stateLabel}
              </span>
            )}
            {project.isLocked && (
              <span
                className="inline-flex items-center gap-1 text-xs bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded px-1.5 py-0.5 font-medium"
                title={lockedWriteBackMessage(project.state)}
              >
                <LockKeyhole className="h-3 w-3" />
                Locked
              </span>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          {local ? (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className={`text-xs border rounded px-1.5 py-0.5 ${
                local.cache_state === 'synchronized'
                  ? 'bg-green-500/10 text-green-600 border-green-500/20'
                  : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20'
              }`}>
                {local.cache_state === 'synchronized' ? 'Synchronized' : 'Requires sync'}
              </span>
              <span className="text-xs text-muted-foreground">
                {local.content_synced_at
                  ? new Date(local.content_synced_at).toLocaleString()
                  : 'Never synchronized'}
              </span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs bg-muted text-muted-foreground border rounded px-1.5 py-0.5">
                Not synchronized
              </span>
            </div>
          )}
        </td>
      </tr>
    );
  };

  const tableHead = (
    <thead className="border-b bg-muted/50">
      <tr>
        <th className="px-4 py-3 text-center font-medium w-12" />
        <th className="px-4 py-3 text-left font-medium">Name</th>
        <th className="px-4 py-3 text-left font-medium">Owner</th>
        <th className="px-4 py-3 text-left font-medium">Department</th>
        <th className="px-4 py-3 text-left font-medium">Indicators</th>
        <th className="px-4 py-3 text-left font-medium">Version</th>
        <th className="px-4 py-3 text-left font-medium">Status</th>
      </tr>
    </thead>
  );

  const pageNumbers = useMemo(() => {
    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push('ellipsis');
      for (let i = Math.max(2, safePage - 1); i <= Math.min(totalPages - 1, safePage + 1); i++) {
        pages.push(i);
      }
      if (safePage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }
    return pages;
  }, [totalPages, safePage]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-6xl py-8 px-4 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Select a project</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {localFavorites.size > 0
                ? `${localFavorites.size} favorite${localFavorites.size !== 1 ? 's' : ''} · ${mergedProjects.length} total`
                : `${mergedProjects.length} project${mergedProjects.length !== 1 ? 's' : ''} available`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onConnect}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh catalog
            </Button>
            {onDisconnect && (isConnected || Boolean(cookie)) && (
              <Button variant="outline" size="sm" onClick={onDisconnect}>
                <LogOut className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            )}
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, owner or department..."
            className="pl-9"
          />
        </div>

        {localLoading ? (
          <p className="text-center text-muted-foreground py-8">Loading projects...</p>
        ) : localError ? (
          <p className="text-center text-destructive py-8">{localError.message}</p>
        ) : filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-8">No projects found</p>
        ) : (
          <div className="space-y-4">
            {favoriteProjects.length > 0 && (
              <>
                <div className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
                  <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                  Favorites ({favoriteProjects.length})
                </div>
                <div className="rounded-md border">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      {tableHead}
                      <tbody className="divide-y">
                        {favoriteProjects.map(renderProjectRow)}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="border-t my-4" />
                <div className="text-sm font-medium text-muted-foreground">
                  All projects ({otherProjects.length})
                </div>
              </>
            )}

            <div className="rounded-md border">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  {tableHead}
                  <tbody className="divide-y">
                    {paged.length === 0 && favoriteProjects.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center text-muted-foreground"
                        >
                          No projects found
                        </td>
                      </tr>
                    ) : paged.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-4 py-8 text-center text-muted-foreground"
                        >
                          No other projects
                        </td>
                      </tr>
                    ) : paged.map(renderProjectRow)}
                  </tbody>
                </table>
              </div>
            </div>

            {otherProjects.length > PAGE_SIZE && (
              <div className="flex items-center justify-between pt-2">
                <p className="text-sm text-muted-foreground">
                  Showing {rangeStart}–{rangeEnd} of {otherProjects.length} projects
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setPage(safePage - 1)}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  {pageNumbers.map((p, index) =>
                    p === 'ellipsis' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">…</span>
                    ) : (
                      <Button
                        key={p}
                        variant={p === safePage ? 'default' : 'ghost'}
                        size="sm"
                        className="min-w-[2rem]"
                        onClick={() => setPage(p)}
                      >
                        {p}
                      </Button>
                    ),
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(safePage + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
